import { useEffect, useRef, useState } from 'react';

/**
 * Photo picker for the Add / Edit person form.
 *
 * Deliberately does NOT upload. It hands the chosen File to the parent, which
 * uploads only after the account exists - the blob key is
 * avatars/<userId>/<version>.jpg, so there is nowhere to put the image until
 * the user has an id.
 */

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

interface AvatarPickerProps {
  file: File | null;
  onChange: (file: File | null) => void;
  /** Initials shown until a photo is chosen. */
  fallback: string;
  /** The already-saved photo, shown when no new file is being picked. */
  currentUrl?: string | null;
  disabled?: boolean;
}

export function AvatarPicker({
  file,
  onChange,
  fallback,
  currentUrl,
  disabled,
}: AvatarPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  /**
   * createObjectURL allocates a blob: URL that lives until it is revoked.
   * Without the cleanup, picking six photos leaks six images for the lifetime
   * of the page.
   */
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function accept(candidate: File | undefined) {
    if (!candidate) return;

    if (!ACCEPTED.includes(candidate.type)) {
      setError('Choose a JPEG, PNG or WebP image');
      return;
    }
    if (candidate.size > MAX_BYTES) {
      setError('Image must be 2 MB or smaller');
      return;
    }

    setError(null);
    onChange(candidate);
  }

  function clear() {
    setError(null);
    onChange(null);
    // Without this, choosing the SAME file again fires no change event.
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <span className="block text-sm font-medium text-slate-700">
        Photo <span className="ml-1 text-slate-400">(optional)</span>
      </span>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!disabled) accept(event.dataTransfer.files[0]);
        }}
        className={[
          'mt-1.5 flex items-center gap-4 rounded-lg border border-dashed px-4 py-3.5 transition',
          dragging ? 'border-brand-500 bg-brand-50' : 'border-slate-300 bg-slate-50/60',
        ].join(' ')}
      >
        {preview || currentUrl ? (
          <img
            src={preview ?? currentUrl ?? undefined}
            alt={preview ? 'Selected photo preview' : 'Your profile photo'}
            className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white"
          />
        ) : (
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-brand-100 text-lg font-semibold text-brand-700">
            {fallback || '?'}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {file ? 'Change photo' : 'Choose photo'}
            </button>
            {file && (
              <button
                type="button"
                disabled={disabled}
                onClick={clear}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-500 transition hover:bg-white hover:text-slate-700 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>

          <p className="mt-1.5 truncate text-xs text-slate-500">
            {file
              ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB`
              : 'Drag one here, or click to browse. JPEG, PNG or WebP, up to 2 MB.'}
          </p>
        </div>
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(event) => accept(event.target.files?.[0])}
      />
    </div>
  );
}
